import { useState, useEffect, useMemo } from "react";
import {
  ShieldCheck,
  Zap,
  Cloud,
  Lock,
  Link,
  Shuffle,
  TrendingUp,
  Server,
  Smartphone,
  CloudCog,
  UserCheck,
  FileCheck,
  DatabaseZap,
  GitMerge,
  SlidersHorizontal,
  LineChart,
  Cpu,
  Shield,
} from "lucide-react";
import { useLocation } from "react-router-dom";
import i18n from "../i18n";

export default function Solutions() {
  const [langTick, setLangTick] = useState(0);
  const location = useLocation();

  useEffect(() => {
    const onLang = () => setLangTick((x) => x + 1);
    i18n.on("languageChanged", onLang);
    return () => i18n.off("languageChanged", onLang);
  }, []);

  const tr = useMemo(() => {
    void langTick;

    return {
      title: i18n.t("solutions.title"),
      subtitle: i18n.t("solutions.subtitle"),

      tabs: {
        migration: i18n.t("solutions.tabs.migration"),
        automation: i18n.t("solutions.tabs.automation"),
        appdev: i18n.t("solutions.tabs.appdev"),
      },

      migration: {
        title: i18n.t("solutions.migration.title"),
        description: i18n.t("solutions.migration.description"),
        features: {
          risk: {
            title: i18n.t("solutions.migration.features.risk.title"),
            desc: i18n.t("solutions.migration.features.risk.desc"),
          },
          arch: {
            title: i18n.t("solutions.migration.features.arch.title"),
            desc: i18n.t("solutions.migration.features.arch.desc"),
          },
          integration: {
            title: i18n.t("solutions.migration.features.integration.title"),
            desc: i18n.t("solutions.migration.features.integration.desc"),
          },
          performance: {
            title: i18n.t("solutions.migration.features.performance.title"),
            desc: i18n.t("solutions.migration.features.performance.desc"),
          },
          security: {
            title: i18n.t("solutions.migration.features.security.title"),
            desc: i18n.t("solutions.migration.features.security.desc"),
          },
          flexibility: {
            title: i18n.t("solutions.migration.features.flexibility.title"),
            desc: i18n.t("solutions.migration.features.flexibility.desc"),
          },
        },
      },

      automation: {
        title: i18n.t("solutions.automation.title"),
        description: i18n.t("solutions.automation.description"),
        features: {
          workflows: {
            title: i18n.t("solutions.automation.features.workflows.title"),
            desc: i18n.t("solutions.automation.features.workflows.desc"),
          },
          testing: {
            title: i18n.t("solutions.automation.features.testing.title"),
            desc: i18n.t("solutions.automation.features.testing.desc"),
          },
          scalable: {
            title: i18n.t("solutions.automation.features.scalable.title"),
            desc: i18n.t("solutions.automation.features.scalable.desc"),
          },
          infrastructure: {
            title: i18n.t("solutions.automation.features.infrastructure.title"),
            desc: i18n.t("solutions.automation.features.infrastructure.desc"),
          },
          analytics: {
            title: i18n.t("solutions.automation.features.analytics.title"),
            desc: i18n.t("solutions.automation.features.analytics.desc"),
          },
          security: {
            title: i18n.t("solutions.automation.features.security.title"),
            desc: i18n.t("solutions.automation.features.security.desc"),
          },
        },
      },

      appdev: {
        title: i18n.t("solutions.appdev.title"),
        description: i18n.t("solutions.appdev.description"),
        features: {
          modern: {
            title: i18n.t("solutions.appdev.features.modern.title"),
            desc: i18n.t("solutions.appdev.features.modern.desc"),
          },
          cloud: {
            title: i18n.t("solutions.appdev.features.cloud.title"),
            desc: i18n.t("solutions.appdev.features.cloud.desc"),
          },
          design: {
            title: i18n.t("solutions.appdev.features.design.title"),
            desc: i18n.t("solutions.appdev.features.design.desc"),
          },
          compliance: {
            title: i18n.t("solutions.appdev.features.compliance.title"),
            desc: i18n.t("solutions.appdev.features.compliance.desc"),
          },
          data: {
            title: i18n.t("solutions.appdev.features.data.title"),
            desc: i18n.t("solutions.appdev.features.data.desc"),
          },
          performance: {
            title: i18n.t("solutions.appdev.features.performance.title"),
            desc: i18n.t("solutions.appdev.features.performance.desc"),
          },
        },
      },
    };
  }, [langTick]);

  const solutions = useMemo(() => {
    void langTick;

    return [
      {
        id: "migration",
        label: tr.tabs.migration,
        title: tr.migration.title,
        description: tr.migration.description,
        features: [
          {
            icon: ShieldCheck,
            title: tr.migration.features.risk.title,
            desc: tr.migration.features.risk.desc,
          },
          {
            icon: Cloud,
            title: tr.migration.features.arch.title,
            desc: tr.migration.features.arch.desc,
          },
          {
            icon: Link,
            title: tr.migration.features.integration.title,
            desc: tr.migration.features.integration.desc,
          },
          {
            icon: Zap,
            title: tr.migration.features.performance.title,
            desc: tr.migration.features.performance.desc,
          },
          {
            icon: Lock,
            title: tr.migration.features.security.title,
            desc: tr.migration.features.security.desc,
          },
          {
            icon: Shuffle,
            title: tr.migration.features.flexibility.title,
            desc: tr.migration.features.flexibility.desc,
          },
        ],
      },

      {
        id: "automation",
        label: tr.tabs.automation,
        title: tr.automation.title,
        description: tr.automation.description,
        features: [
          {
            icon: GitMerge,
            title: tr.automation.features.workflows.title,
            desc: tr.automation.features.workflows.desc,
          },
          {
            icon: SlidersHorizontal,
            title: tr.automation.features.testing.title,
            desc: tr.automation.features.testing.desc,
          },
          {
            icon: TrendingUp,
            title: tr.automation.features.scalable.title,
            desc: tr.automation.features.scalable.desc,
          },
          {
            icon: Cpu,
            title: tr.automation.features.infrastructure.title,
            desc: tr.automation.features.infrastructure.desc,
          },
          {
            icon: LineChart,
            title: tr.automation.features.analytics.title,
            desc: tr.automation.features.analytics.desc,
          },
          {
            icon: Shield,
            title: tr.automation.features.security.title,
            desc: tr.automation.features.security.desc,
          },
        ],
      },

      {
        id: "app-dev",
        label: tr.tabs.appdev,
        title: tr.appdev.title,
        description: tr.appdev.description,
        features: [
          {
            icon: Smartphone,
            title: tr.appdev.features.modern.title,
            desc: tr.appdev.features.modern.desc,
          },
          {
            icon: CloudCog,
            title: tr.appdev.features.cloud.title,
            desc: tr.appdev.features.cloud.desc,
          },
          {
            icon: UserCheck,
            title: tr.appdev.features.design.title,
            desc: tr.appdev.features.design.desc,
          },
          {
            icon: FileCheck,
            title: tr.appdev.features.compliance.title,
            desc: tr.appdev.features.compliance.desc,
          },
          {
            icon: DatabaseZap,
            title: tr.appdev.features.data.title,
            desc: tr.appdev.features.data.desc,
          },
          {
            icon: Server,
            title: tr.appdev.features.performance.title,
            desc: tr.appdev.features.performance.desc,
          },
        ],
      },
    ];
  }, [langTick, tr]);

  const [activeSolution, setActiveSolution] = useState(solutions[0]);

  useEffect(() => {
    setActiveSolution(solutions[0]);
  }, [langTick]);

  useEffect(() => {
    const hash = location.hash.replace("#", "");
    const matched = solutions.find((s) => s.id === hash);
    if (matched) {
      setActiveSolution(matched);

      const el = document.getElementById("solutions");
      if (el) {
        setTimeout(() => {
          el.scrollIntoView({ behavior: "smooth" });
        }, 100);
      }
    }
  }, [location.hash, langTick, solutions]);

  return (
    <div
      id="solutions"
      className="bg-gray-100 min-h-[calc(100vh+100px)] md:min-h-[90vh] text-gray-900 pt-16 pb-16 md:pb-4 px-6 md:px-12 lg:px-24"
    >
      <h2 className="max-w-6xl mx-auto text-left text-4xl font-bold mb-4 mt-12">
        {tr.title}
      </h2>

      <p className="max-w-6xl mx-auto text-left mb-12">{tr.subtitle}</p>

      <div className="max-w-6xl mx-auto text-left">
        <div className="mb-6">
          <div className="inline-flex space-x-0 pb-3">
            {solutions.map((solution) => (
              <button
                key={solution.id}
                onClick={() => setActiveSolution(solution)}
                className={`px-4 pb-2 transition text-sm md:text-base relative after:absolute after:left-0 after:bottom-0 after:w-full after:h-[2px] after:rounded-full ${
                  activeSolution.id === solution.id
                    ? "text-indigo-600 after:bg-indigo-600 after:h-[3px] font-semibold"
                    : "text-gray-700 after:bg-gray-400 after:opacity-50 hover:after:opacity-100"
                }`}
                type="button"
              >
                {solution.label}
              </button>
            ))}
          </div>
        </div>

        <div
          id={activeSolution.id}
          key={activeSolution.id}
          className="bg-white rounded-2xl p-8 shadow-lg animate-fadeIn"
        >
          <h3 className="text-3xl font-medium mb-4">{activeSolution.title}</h3>
          <p className="mb-8 pb-6 border-b border-gray-300 text-lg">
            {activeSolution.description}
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {activeSolution.features.map((feature, idx) => (
              <div key={idx} className="flex items-start gap-4">
                <div className="bg-indigo-100 p-4 rounded-full mb-4">
                  <feature.icon className="text-indigo-500 text-2xl" />
                </div>
                <div>
                  <h4 className="font-semibold text-lg">{feature.title}</h4>
                  <p className="text-base opacity-80">{feature.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}


























